import ActionModal from "components/ActionModal";
import IncomingParcelWizard from "components/IncomingParcelWizard";
import ApplicationLayout from "components/ApplicationLayout";
import CreateCaseModal from "components/CreateCaseModal";
import CreateDossierModal from "components/CreateDossierModal";
import CreateLetterModal from "components/CreateLetterModal";
import CreateOutgoingParcelModal from "components/CreateOutgoingParcelModal";
import ProtectedRoute from "components/ProtectedRoute";
import BankingCallbackHandler from "components/Banking/BankingCallbackHandler";
import CaseChatContext from "contexts/CaseChatContext";
import CasesContext from "contexts/CasesContext";
import DecryptedMsgs, { IDecryptedMsg } from "contexts/DecryptedMsgs";
import DossierFilesContext from "contexts/DossierFilesContext";
import ModalsManagerContext, {
  defaultValue,
  IModalOptions,
  IModalsManager,
  BudgetEngagementModalOptions,
} from "contexts/ModalsManager";
import PDFContext, { IPDF } from "contexts/PDFContext";
import PackagesContext from "contexts/PackagesContext";
import { IDossierItem } from "dto/Dossier/IDossierItem";
import { IPackage } from "dto/IPackage";
import PDFModule from "modules/PDFModule";
import ActionsRoute from "modules/actionsModule/Actions.route";
import CasesRoute from "modules/casesModule/Cases.route";
import ClientsRoute from "modules/clientsModule/Clients.route";
import DeskRoute from "modules/deskModule/Desk.route";
import MessagesRoute from "modules/packageModule/Messages.route";
import AcceptanceSignRoute from "modules/acceptanceSignModule/acceptanceSign.route";
import DecreesRoute from "modules/decreesModule/decrees.route";
import Recovery from "modules/recovery";
import { useContext, useEffect, useMemo, useState } from "react";
import { NotificationContainer } from "react-notifications";
import { getUserId } from "utils/getUserId";
import { getKeys } from "utils/getKeys";
import { QueryClient, QueryClientProvider } from "react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { plPL } from "@mui/x-date-pickers/locales";
import Login from "components/Login";
import PluginContext from "components/PluginContext";
import SettingsModal from "components/Settings/SettingsModal";
import DeepLinkHandler from "components/DeepLinkHandler";
import Unauthorized from "components/Unauthorized";
import ActionsContext from "contexts/ActionsContext";
import CertContext, { ICert } from "contexts/CertContext";
import ConverstationContext from "contexts/ConversationContext";
import KeysContext from "contexts/KeysContext";
import LettersContext from "contexts/LettersContext";
import MenuContext from "contexts/MenuContext";
import { ICaseSimple } from "dto/Cases/ICaseSimple";
import { IFeed } from "dto/IFeed";
import { IKey } from "dto/IKey";
import ActivationRoute from "modules/activationModule/Activation.route";
import CasesFullScreenRoute from "modules/casesModule/Cases.FullScreen.route";
import React from "react";
import { useAuth } from "react-oidc-context";
import { createSignalRContext } from "react-signalr/signalr";
import "./styles/notification.css";

import { HubCallbacksNames } from "api/hub";
import EditClientModalNew from "components/EditClientModalNew";
import { ErrorBoundary } from "components/ErrorBoundary";
import InvitationToCaseManager from "components/InvitationToCaseManager";
import KeysManager from "components/KeysManager";
import Loading from "components/Loading";
import NotificationManager from "components/NotificationManager";
import SettingsManager from "components/SettingsManager";
import Spinner from "components/Spinner";
import ClientsContext from "contexts/ClientsContext";
import WorkspaceContext from "contexts/CurrentWorkspace";
import LoadingContext from "contexts/LoadingContext";
import NotificationContext from "contexts/NotificationsContext";
import CPluginContext, { IPlugin } from "contexts/PluginContext";
import SettingsContext, { IOptions } from "contexts/SettingsContext";
import { IClient } from "dto/IClient";
import { INotification } from "dto/INotification";
import { IWorkspace } from "dto/IWorkspace";
import ChooseWorkspace from "modules/ChooseWorkspace";
import { ExportModule } from "modules/ExportModule";
import InvitationModule from "modules/InvitationModule";
import { ThreadModule } from "modules/ThreadModule";
import { FolderModule } from "modules/folderModule";
import LetterModule from "modules/letterModule";
import RegisterModule from "modules/registerModule";
import TemplatesRoutes from "modules/templatesModule/Templates.route";
import "moment/locale/pl";
import { ENotificationType, notification } from "utils/notification";
import { setWorkspaceId } from "utils/setWorkspaceId";
import { installAppSettingsMirror, hydrateAppSettings } from "utils/appSettingsSync";
import {
  setCompanyPrivateKeys,
  clearCompanyContentKeys,
} from "utils/companyContentKeys";
import {
  pickActiveCompanyKey,
  setActiveCompanyKey,
  clearActiveCompanyKey,
} from "utils/encryptForCompany";
import { decryptCompanyPrivateKeys } from "utils/decryptCompanyPrivateKeys";
import companyKeysService from "api/companyKeysService";

import { LicenseInfo } from "@mui/x-license";
import AttachToCaseModalNew from "components/AttachCaseModalNew";
import CreateTaskModal from "components/CreateTaskModal";
import EditStatusModal from "components/EditStatusModal";
import EditTagModal from "components/EditTagModal";
import ModalsManager from "components/ModalsManager";
import DiskSpaceModal from "components/DiskSpaceModal";
import TrashMovedModal from "components/TrashMovedModal";
import EventContext from "contexts/EventContext";
import SignalsContext from "contexts/SignalsContext";
import DriveRoute from "modules/driveModule/Drive.route";
import SharesRoute from "modules/sharesModule/Shares.route";
import * as Sentry from "@sentry/react";
import { ContactFormWizard } from "components/ContactFormWizard/public";

LicenseInfo.setLicenseKey(
  "6ad75d9b9f4ade1990209831c129f5aaTz0xMjkxMzYsRT0xODEwNzcxMTk5MDAwLFM9cHJlbWl1bSxMTT1hbm51YWwsUFY9aW5pdGlhbCxLVj0y",
);

if (!import.meta.env.DEV && import.meta.env.VITE_ENV !== "dev") {
  Sentry.init({
    dsn: "https://b2d42b2b6b65641d0e8b2951dd2315a5@sn.notaup.pl/3",
    environment: import.meta.env.VITE_ENV,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        unmask: [".sentry-unmask, [data-sentry-unmask]"],
      }),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      /^https:\/\/beta.notaup\.pl\/api/,
      /^https:\/\/notaup\.pl\/api/,
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

import "dayjs/locale/pl";
import DocumentViewModule from "modules/DocumentViewModule";
import AddAddressModal from "components/AddAddressModal";
import AddClientInClientsModal from "components/AddClientInClientsModal";
import EditPDFName from "components/EditPDFName";
import usePrivilegesStore from "hooks/usePrivilegesStore";
import { axiosInstance } from "api/axios";
import { AbilityContext, defineAbilityFor } from "contexts/AbilityContext";
import PackageThreadContext from "contexts/PackageThreadContext";
import TransferAdeRoute from "modules/transferAdeModule/transferAde.route";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import AdminRoute from "modules/adminModule/admin.route";
import WorkflowRoute from "modules/workflowModule/Workflow.route";
import RegistryRoute from "modules/registryModule/registry.route";
import BiuroPodawczeRoute from "modules/biuroPodawczeModule/biuroPodawcze.route";
import CouncilRoute from "modules/councilModule/council.route";
import LocalLawRoute from "modules/localLawModule/localLaw.route";
import EgibRoute from "modules/egibModule/egib.route";
import ProcurementRoute from "modules/procurementModule/procurement.route";
import WasteMapRoute from "modules/wasteMapModule/wasteMap.route";
import BudgetRoute from "modules/budgetModule/budget.route";
import CreateBudgetExpenseModal from "components/Budget/CreateBudgetExpenseModal";
import CreateBudgetEngagementSlideModal from "components/Budget/CreateBudgetEngagementSlideModal";
import FirstActivationWizard from "components/FirstActivationWizard";
import DebugView from "components/DebugView";
import CreateHelpdeskIssueModal from "components/CreateHelpdeskIssueModal";
import HelpdeskRoute from "modules/helpdeskModule/helpdesk.route";
import IssuesContext from "contexts/IssuesContext";
import { IIssue } from "dto/Helpdesk/IIssue";
import ChatMessagesContext from "contexts/ChatMessagesContext";
import { IChatMessage } from "dto/Chat/IChatMessage";
import CreateChatModal from "components/CreateChatModal";
import ChatsContext from "contexts/ChatsContext";
import { IChat } from "dto/Chat/IChat";
import ChatRoute from "modules/chatModule/chat.route";
import AddressBookVerificationsContext from "contexts/AddressBookVerificationsContext";
import { IAddressBookVerificationListItem } from "dto/Admin/IAddressBookVerification";
import { HttpTransportType } from "@microsoft/signalr";
import AppContext from "contexts/AppContext";
import { RedirectModal } from "components/RedirectModal";
import DecreeModal from "components/DecreeModal";
import GenerateLettersModal from "components/GenerateLettersModal";
import DispatchModal from "components/DispatchModal";
import ConfirmPickupModal from "components/ConfirmPickupModal";
import RegisterReceptionModal from "components/RegisterReceptionModal";
import RecoveryCodeModal from "components/RecoveryCodeModal";
// Kontekst FIRMY (docs/model-kluczy-firmowych.md, F8) — ZASTĘPUJE UserProfileContext.
// Profile znikły (D1): zapis idzie przez klucz firmy (utils/encryptForCompany), odczyt
// przez mirror kluczy treści (utils/companyContentKeys). CompanyAbilityProvider (niżej)
// montuje AbilityContext WEWNĄTRZ tego dostawcy, żeby CASL widziało też uprawnienia
// firmowe (EPrivileges), nie tylko moduły.
import CompanyProvider from "components/CompanyProvider";
import CompanyContext from "contexts/CompanyContext";
import ContactFormVerificationRoute from "modules/contactFormVerificationModule/contactFormVerification.route";
import ReactGA from "react-ga4";
import RemoveAccountModule from "modules/removeAccountModule";
import CourtPortalRoute from "modules/courtPortalModule/courtPortal.route";
import CreateInvoiceModal from "components/CreateInvoiceModal";
import CreateRegistryEntryModal from "components/CreateRegistryEntryModal";
import CreateInvoiceRegistryModal from "components/CreateInvoiceRegistryModal";
import ContactFormModule from "modules/contactFormModule";

// const navigate = useNavigate();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

export const SignalRContext = createSignalRContext({
  shareConnectionBetweenTab: true,
});

ReactGA.initialize("G-2QFDMXNQEL", {
  testMode: import.meta.env.VITE_ENV === "dev",
});

/**
 * Montuje AbilityContext jako POTOMEK CompanyProvider (docs/model-kluczy-firmowych.md, F8).
 * `ability` liczony wprost w ciele App() nie ma dostępu do CompanyContext, bo CompanyProvider
 * jest montowany NIŻEJ w zwracanym JSX — ten komponent jest realnym potomkiem dostawcy,
 * więc widzi `privileges` firmy.
 */
const CompanyAbilityProvider: React.FC<{
  modules: string[];
  children: React.ReactNode;
}> = ({ modules, children }) => {
  const auth = useAuth();
  const { privileges: companyPrivileges } = useContext(CompanyContext);

  const ability = useMemo(
    () => defineAbilityFor(auth?.user, modules, companyPrivileges),
    [auth?.user, modules, companyPrivileges],
  );

  return (
    <AbilityContext.Provider value={ability}>
      {children}
    </AbilityContext.Provider>
  );
};

export function App() {
  const auth = useAuth();
  const [modals, setModals] = useState<IModalsManager>(defaultValue);

  const [modalOptions, setModalOptions] = useState<IModalOptions>({});

  const [pdf, setPDF] = useState<IPDF>({
    page: 1,
    zoom: 1,
    annotation: null,
    showAnnotation: null,
    canvas: null,
    editComment: "",
    annotationToAdd: null,
    annotations: [],
    rotate: 0,
    drawLine: false,
    drawPin: false,
    drawHighlight: false,
  });

  const [workspace, setWorkspace] = useState<IWorkspace | null>(null);
  const [notis, setNotification] = useState<INotification[]>([]);
  const [settings, setSettings] = useState<IOptions[]>([]);
  const [packages, setPackages] = useState<IPackage[]>([]);
  const [issues, setIssues] = useState<IIssue[]>([]);
  const [addressBookVerifications, setAddressBookVerifications] = useState<
    IAddressBookVerificationListItem[]
  >([]);
  const [chats, setChats] = useState<IChat[]>([]);
  const [chatMessages, setChatMessages] = useState<IChatMessage[]>([]);
  const [threadPackages, setThreadPackages] = useState<IPackage[]>([]);
  const [keys, setKeys] = useState<IKey[]>([]);
  const [cases, setCases] = useState<ICaseSimple[]>([]);
  const [tasks, setTasks] = useState<IFeed[]>([]);
  const [casePackages, setCasePackages] = useState<IPackage[]>([]);
  const [decryptedMsgs, setDecryptedMsgs] = useState<IDecryptedMsg[]>([]);
  const [dossierFiles, setDossierFiles] = useState<IDossierItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [letters, setLetters] = useState<IPackage[]>([]);
  const [clients, setClients] = useState<IClient[]>([]);
  const [plugin, setPlugin] = useState<IPlugin>({
    actual: true,
    version: "0",
    linkWindows: "",
    linkMac: "",
    isMac: false,
    isWindows: false,
  });

  const [activePanel, setActivePanel] = useState<string>(null);

  const [filterId, setFilterId] = useState("");

  const [converstation, setConverstation] = useState<IPackage[]>([]);
  const [id, setId] = useState("");
  const [parent, setParent] = useState("");
  const [children, setChildren] = useState("");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- callback o zmiennej sygnaturze, ustawiany przez komponent potomny
  const [callback, setCallback] = useState<any>(() => {});

  const [cert, setCert] = useState<ICert>({
    id: "",
    name: "",
    versionId: "",
  });

  const isGuest = auth?.user?.profile?.role === "guest";

  const initPrivilegesStore = usePrivilegesStore((state) => state.init);
  const privileges = usePrivilegesStore((state) => state.privileges);
  const ability = defineAbilityFor(auth?.user, privileges);

  //const { mutate: getPrivilages } = userService.useGetPrivileges();

  SignalRContext.useSignalREffect(
    HubCallbacksNames.NewMessage,
    (message) => {
      if (message.items.length === 1) {
        if (message.items[0].isIncoming) {
          notification({
            title: `Nowa wiadomość`,
            text: `Nowa wiadomość od: ${message.items[0].sender}`,
            type: ENotificationType.INFO,
          });
        }
      }
    },
    [],
  );

  SignalRContext.useSignalREffect(
    HubCallbacksNames.Offline,
    () => {
      // notification({
      //   title: `Użytkownik OFFLINE`,
      //   text: `Inny użytkownik OFFLINE: ${message.userId}`,
      //   type: ENotificationType.ERROR,
      // });
    },
    [],
  );

  const removeDuplicates = (keys: IKey[]) => {
    return keys.filter(
      (item, index, array) =>
        array.findIndex((i) => i?.Id === item?.Id) === index,
    );
  };

  useEffect(() => {
    setInterval(() => {
      setKeys((prev) => {
        const hasDuplicates = prev.some(
          (item, index) => prev.findIndex((i) => i?.Id === item?.Id) !== index,
        );

        if (!hasDuplicates) {
          return prev;
        }
        return removeDuplicates(prev).filter((f) => f !== undefined);
      });
    }, 5000);
  }, []);

  useEffect(() => {}, [keys]);

  useEffect(() => {
    if (auth.user && auth.isAuthenticated) {
      Sentry.setUser({
        id: auth.user?.profile?.sub,
        username: auth?.user?.profile?.name,
        email: auth?.user?.profile?.email,
      });

      ReactGA.set({ userId: auth.user?.profile?.sub });

      if (window._uxa) {
        window._uxa.push(["identify", auth.user?.profile?.sub]);
      }

      if (window.CS_EVENT) {
        window.CS_EVENT.identify = auth.user?.profile?.sub;
      }

      axiosInstance
        .get("/ui/privileges")
        .then((r) => {
          initPrivilegesStore(r.data.Modules);
        })
        .catch(() => {});

      // Trwałość danych dotąd „localStorage-only" (struktura org., słowniki,
      // profil firmy, dziennik ZOiS, atrybuty klasyfikacji…): mirror zapisów do
      // BE + hydratacja localStorage z BE / migracja LS→BE przy pierwszym logowaniu.
      installAppSettingsMirror();
      hydrateAppSettings();
      //   // userService.()getPrivilages(null, {
      //   //   onSuccess(data, variables, context) {
      //   //     privilegesStore.setState({
      //   //       name: data.data.Name,
      //   //       privileges: data.data.Modules,
      //   //     });
      //   //   },
      //   // });
    } else {
      Sentry.setUser(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- celowo reagujemy tylko na zmianę auth.user
  }, [auth.user]);

  useEffect(() => {
    if (!workspace) return;
    setWorkspaceId(workspace.Id);
  }, [workspace]);

  // const location = useLocation();

  // useEffect(() => {
  //   if (window.CS_CONF) {
  //     window.CS_CONF.page = location.pathname;
  //   }
  //   if (window.CS && window.CS.pageUpdated) {
  //     window.CS.pageUpdated();
  //   }
  // }, [location]);

  useEffect(() => {
    const notify = () => {
      if (window.CS?.pageUpdated) window.CS.pageUpdated();
      if (window.CS_CONF) window.CS_CONF.page = window.location.pathname;
    };

    const pushState = history.pushState;
    history.pushState = function (...args) {
      const result = pushState.apply(this, args);
      notify();
      return result;
    };

    const replaceState = history.replaceState;
    history.replaceState = function (...args) {
      const result = replaceState.apply(this, args);
      notify();
      return result;
    };

    window.addEventListener("popstate", notify);

    return () => {
      history.pushState = pushState;
      history.replaceState = replaceState;
      window.removeEventListener("popstate", notify);
    };
  }, []);

  // todo - na później, jeżeli ostatnie ruchy myszką były w przeciągu 10 minut
  // to odświeżamy automatycznie, jeżeli nie to pokazujemy komunikat dla usera,
  // że zostanie niedługo wylogowany.

  const [blockList, setBlockList] = useState(false);
  const [manager, setManager] = useState<IPackage | null>(null);

  // Priming slotu 'company' mirrora kluczy treści ORAZ tożsamości aktywnej generacji klucza
  // firmy (docs/model-kluczy-firmowych.md, sekcja 3). Bez tego pierwszy zapis/odczyt sesji
  // trafiałby wyłącznie w samonaprawiający się fallback (`requireActive()` w
  // utils/encryptForCompany przy zapisie; brak materiału przy odczycie).
  //
  // Granty bez wydanego klucza (`MyGrant === null`, konto czeka na wydanie klucza firmy) to
  // stan POPRAWNY — nie logujemy go jako błąd; nieodszyfrowywalny grant loguje sama
  // decryptCompanyPrivateKeys (FailedGenerations).
  React.useEffect(() => {
    if (!auth.user || !auth.isAuthenticated) {
      clearActiveCompanyKey();
      clearCompanyContentKeys();
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await companyKeysService.getCompanyKeys();
        if (cancelled) return;

        const chosen = pickActiveCompanyKey(data);
        await setActiveCompanyKey(
          chosen
            ? {
                WorkspaceId: chosen.WorkspaceId,
                WorkspaceKeyId: chosen.Id,
                Generation: chosen.Generation,
                PublicKeyBase64: chosen.PublicKey,
              }
            : null,
        );
        if (cancelled) return;

        const userId = getUserId(auth.user!);
        const myKeys = await getKeys(userId);
        if (cancelled || !myKeys) return;

        const { Keys } = await decryptCompanyPrivateKeys(data?.Keys, myKeys);
        if (!cancelled) setCompanyPrivateKeys(Keys);
      } catch {
        // Brak firmy / firma bez jeszcze wydanego klucza — zapis zgłosi to jawnie przez
        // CompanyKeyMissingError (fail-closed), nie cichym fallbackiem.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reagujemy tylko na zmianę auth.user
  }, [auth.user]);

  React.useEffect(() => {
    return auth.events.addSilentRenewError(() => {});
  }, [auth.events, auth.signinSilent]);
  switch (auth.activeNavigator) {
    case "signinSilent":
      if (!auth.user || !auth.isAuthenticated) {
        return <div>Trwa logowanie...</div>;
      }
      break;
    case "signoutRedirect":
      return <div>Przekierowanie do wylogowania...</div>;
    default:
    //
  }

  // if (auth.) {
  //   return <div>Trwa ładowanie...</div>;
  // }

  if (auth.error && !auth.isAuthenticated) {
    return <div>Oops... {auth.error.message}</div>;
  }

  // useEffect(() => {
  //   NotificationManager.error('Info message', 'tes', 99999999999);
  // }, []);

  const h = window.location.href;
  const isInvitation = h.search("invitation") !== -1;
  const isLogin = h.search("login") !== -1;
  const isLoginTest = h.search("signup") !== -1;
  const isRegister = h.search("register") !== -1;
  const isContactFormVerification =
    h.search("contactFormVerification") !== -1;
  const isContactForm = h.search("contactForm") !== -1;
  const isRemoveAccount = h.search("RemoveAccount") !== -1;
  const isRecovery = h.search("recoverqr") !== -1;

  if (isRecovery) {
    return (
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <Routes>
            <Route
              path="/recoverqr/:uId"
              element={
                <>
                  <Recovery />
                </>
              }
            />
          </Routes>
        </QueryClientProvider>
      </BrowserRouter>
    );
  }

  if (isInvitation) {
    return (
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <Routes>
            <Route
              path="/invitation/:id/:type?"
              element={
                <>
                  <InvitationModule notLogged={true} />
                  <NotificationContainer />
                </>
              }
            />
          </Routes>
        </QueryClientProvider>
      </BrowserRouter>
    );
  }

  if (isRegister) {
    return (
      <SignalRContext.Provider
        url={`${import.meta.env.VITE_APP_SITE}/wsRegister`}
      >
        <BrowserRouter>
          <QueryClientProvider client={queryClient}>
            <Routes>
              <Route
                path="/register"
                element={<RegisterModule SignalRContext={SignalRContext} />}
              />
              <Route
                path="/register/:unlockCode"
                element={<RegisterModule SignalRContext={SignalRContext} />}
              />
              <Route
                path="/register/confirm/:linkToken/:linkCode"
                element={<RegisterModule SignalRContext={SignalRContext} />}
              />
            </Routes>
          </QueryClientProvider>
        </BrowserRouter>
      </SignalRContext.Provider>
    );
  }

  if (isRemoveAccount) {
    return (
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <Routes>
            <Route
              path="/RemoveAccount/:id/:token?"
              element={
                <>
                  <RemoveAccountModule />
                  <NotificationContainer />
                </>
              }
            />
          </Routes>
        </QueryClientProvider>
      </BrowserRouter>
    );
  }

  if (isContactFormVerification) {
    return (
      <SignalRContext.Provider
        url={`${import.meta.env.VITE_APP_API_WSREGISTER}`}
      >
        <BrowserRouter>
          <QueryClientProvider client={queryClient}>
            <Routes>
              <Route
                path="/contactFormVerification/*"
                element={<ContactFormVerificationRoute />}
              />
            </Routes>
          </QueryClientProvider>
        </BrowserRouter>
      </SignalRContext.Provider>
    );
  }

  if (isContactForm) {
    return (
      <SignalRContext.Provider
        url={`${import.meta.env.VITE_APP_API_URL.replace(
          "/api",
          "",
        )}/wsContactForm`}
      >
        <BrowserRouter>
          <QueryClientProvider client={queryClient}>
            <AbilityContext.Provider value={ability}>
              <PackagesContext.Provider
                value={{
                  packages,
                  setPackages,
                }}
              >
                <KeysContext.Provider value={{ keys, setKeys }}>
                  <Routes>
                    <Route
                      path="/contactForm"
                      element={
                        <ContactFormModule SignalRContext={SignalRContext} />
                      }
                    />
                    <Route
                      path="/contactForm/:token"
                      element={
                        <ContactFormModule SignalRContext={SignalRContext} />
                      }
                    />
                  </Routes>
                </KeysContext.Provider>
              </PackagesContext.Provider>
            </AbilityContext.Provider>
          </QueryClientProvider>
        </BrowserRouter>
      </SignalRContext.Provider>
    );
  }

  if (isLogin && !auth.isLoading && !(auth.user && auth.isAuthenticated)) {
    return <Login></Login>;
  }

  if (isLoginTest) {
    return <Unauthorized redirect={false} />;
  }

  if (auth.user && auth.isAuthenticated) {
    return (
      <AbilityContext.Provider value={ability}>
        <EventContext.Provider
          value={{
            blockClick: blockList,
            setBlockClick: setBlockList,
            manager,
            setManager,
          }}
        >
          <ErrorBoundary>
            <LoadingContext.Provider
              value={{
                isLoading: loading,
                setIsLoading: setLoading,
              }}
            >
              <LocalizationProvider
                adapterLocale={"plPL"}
                localeText={
                  plPL.components.MuiLocalizationProvider.defaultProps
                    .localeText
                }
                dateAdapter={AdapterMoment}
              >
                <DndProvider backend={HTML5Backend}>
                  <SignalRContext.Provider
                    connectEnabled={!!auth.user.access_token}
                    accessTokenFactory={() => auth.user!.access_token}
                    dependencies={[auth.user.access_token]}
                    skipNegotiation={true}
                    transport={HttpTransportType.WebSockets}
                    url={
                      import.meta.env.VITE_APP_API_WS ??
                      "https://beta.notaup.pl/hub"
                    }
                  >
                    <BrowserRouter>
                      <QueryClientProvider client={queryClient}>
                        <ModalsManagerContext.Provider
                          value={{
                            modals,
                            options: modalOptions,
                            setModals,
                            setOptions: setModalOptions,
                          }}
                        >
                          <SignalsContext.Provider
                            value={{
                              callback,
                              setCallback,
                              children,
                              parent,
                              setChildren,
                              setParent,
                            }}
                          >
                            <CPluginContext.Provider
                              value={{ plugin, setPlugin }}
                            >
                              <AppContext.Provider
                                value={{
                                  activePanel,
                                  setActivePanel,
                                }}
                              >
                                <CompanyProvider>
                                  <CompanyAbilityProvider modules={privileges}>
                                  <WorkspaceContext.Provider
                                    value={{
                                      workspace,
                                      setWorkspace,
                                      filterId,
                                      setFilterId,
                                    }}
                                  >
                                    <MenuContext.Provider value={{ id, setId }}>
                                      <CertContext.Provider
                                        value={{ cert, setCert }}
                                      >
                                        <KeysContext.Provider
                                          value={{ keys, setKeys }}
                                        >
                                          <ActionsContext.Provider
                                            value={{ tasks, setTasks }}
                                          >
                                            <NotificationContext.Provider
                                              value={{
                                                notifications: notis,
                                                setNotifications:
                                                  setNotification,
                                              }}
                                            >
                                                <AddressBookVerificationsContext.Provider
                                                  value={{
                                                    verifications:
                                                      addressBookVerifications,
                                                    setVerifications:
                                                      setAddressBookVerifications,
                                                  }}
                                                >
                                                <IssuesContext.Provider
                                                  value={{ issues, setIssues }}
                                                >
                                                  <ChatsContext.Provider
                                                    value={{
                                                      chats: chats,
                                                      setChats: setChats,
                                                    }}
                                                  >
                                                    <ChatMessagesContext.Provider
                                                      value={{
                                                        messages: chatMessages,
                                                        setMessages:
                                                          setChatMessages,
                                                      }}
                                                    >
                                                      <PackagesContext.Provider
                                                        value={{
                                                          packages,
                                                          setPackages,
                                                        }}
                                                      >
                                                        <PackageThreadContext.Provider
                                                          value={{
                                                            threadPackages,
                                                            setThreadPackages,
                                                          }}
                                                        >
                                                          <ClientsContext.Provider
                                                            value={{
                                                              clients,
                                                              setClients,
                                                            }}
                                                          >
                                                            <LettersContext.Provider
                                                              value={{
                                                                letters,
                                                                setLetters,
                                                              }}
                                                            >
                                                              <CasesContext.Provider
                                                                value={{
                                                                  cases,
                                                                  setCases,
                                                                }}
                                                              >
                                                                <ConverstationContext.Provider
                                                                  value={{
                                                                    converstation,
                                                                    setConverstation,
                                                                  }}
                                                                >
                                                                  <CaseChatContext.Provider
                                                                    value={{
                                                                      casePackages,
                                                                      setCasePackages,
                                                                    }}
                                                                  >
                                                                    <DossierFilesContext.Provider
                                                                      value={{
                                                                        dossierFiles,
                                                                        setDossierFiles,
                                                                      }}
                                                                    >
                                                                      <DecryptedMsgs.Provider
                                                                        value={{
                                                                          decryptedMsgs,
                                                                          setDecryptedMsgs,
                                                                        }}
                                                                      >
                                                                        <PDFContext.Provider
                                                                          value={{
                                                                            PDF: pdf,
                                                                            setPDF:
                                                                              setPDF,
                                                                          }}
                                                                        >
                                                                          <SettingsContext.Provider
                                                                            value={{
                                                                              options:
                                                                                settings,
                                                                              setOptions:
                                                                                setSettings,
                                                                            }}
                                                                          >
                                                                            <PluginContext />
                                                                            <KeysManager />
                                                                            <NotificationManager />
                                                                            <SettingsManager />
                                                                            <ModalsManager />
                                                                            <DiskSpaceModal />
                                                                            <TrashMovedModal />
                                                                            <InvitationToCaseManager
                                                                              SignalRContext={
                                                                                SignalRContext
                                                                              }
                                                                            />
                                                                            <ProtectedRoute
                                                                              SignalRContext={
                                                                                SignalRContext
                                                                              }
                                                                            >
                                                                              <>
                                                                                <BankingCallbackHandler />
                                                                                <Routes>
                                                                                <Route
                                                                                  path="/packages/*"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <MessagesRoute />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />
                                                                                <Route
                                                                                  path="/acceptance-sign/*"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <AcceptanceSignRoute />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />
                                                                                <Route
                                                                                  path="/decrees/*"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <DecreesRoute />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />
                                                                                <Route
                                                                                  path="/fullscreen/cases/*"
                                                                                  element={
                                                                                    <CasesFullScreenRoute />
                                                                                  }
                                                                                />
                                                                                <Route
                                                                                  path="/folder/:caseId/:folderId"
                                                                                  element={
                                                                                    <FolderModule />
                                                                                  }
                                                                                />
                                                                                <Route
                                                                                  path="/thread/:caseId/:threadId"
                                                                                  element={
                                                                                    <ThreadModule />
                                                                                  }
                                                                                />
                                                                                <Route
                                                                                  path="/cases/*"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <CasesRoute />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />
                                                                                <Route
                                                                                  path="/clients/*"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <ClientsRoute />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />
                                                                                <Route
                                                                                  path="/templates/*"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <TemplatesRoutes />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />
                                                                                <Route
                                                                                  path="/desk/*"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <DeskRoute />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />
                                                                                <Route
                                                                                  path="/drive/*"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <DriveRoute />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />
                                                                                <Route
                                                                                  path="/shares"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <SharesRoute />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />
                                                                                <Route
                                                                                  path="/pdf/:kind/:packageId/:PDFId"
                                                                                  element={
                                                                                    <PDFModule />
                                                                                  }
                                                                                />
                                                                                <Route
                                                                                  path="/document"
                                                                                  element={
                                                                                    <DocumentViewModule />
                                                                                  }
                                                                                />
                                                                                <Route
                                                                                  path="/letter-preview/:caseId/:kind/:id/:letterId/:encryptionKey"
                                                                                  element={
                                                                                    <LetterModule />
                                                                                  }
                                                                                />
                                                                                <Route
                                                                                  path="/recovery"
                                                                                  element={
                                                                                    <Recovery />
                                                                                  }
                                                                                />
                                                                                <Route
                                                                                  path="/actions/*"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <ActionsRoute />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />
                                                                                <Route
                                                                                  path="/choose-workspace"
                                                                                  element={
                                                                                    <ChooseWorkspace />
                                                                                  }
                                                                                />
                                                                                <Route
                                                                                  path="/debug"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <DebugView />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />
                                                                                <Route
                                                                                  path="/activation/*"
                                                                                  element={
                                                                                    <ActivationRoute />
                                                                                  }
                                                                                />
                                                                                <Route
                                                                                  path="/transferAde/*"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <TransferAdeRoute />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />
                                                                                <Route
                                                                                  path="/invitation/:id/:type"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <InvitationModule />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />
                                                                                <Route
                                                                                  path="/export"
                                                                                  element={
                                                                                    <ExportModule />
                                                                                  }
                                                                                />

                                                                                <Route
                                                                                  path="/admin/*"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <AdminRoute />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />

                                                                                <Route
                                                                                  path="/workflow/*"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <WorkflowRoute />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />

                                                                                <Route
                                                                                  path="/registry/*"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <RegistryRoute />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />

                                                                                <Route
                                                                                  path="/biuro-podawcze/*"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <BiuroPodawczeRoute />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />

                                                                                <Route
                                                                                  path="/biuro-rady/*"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <CouncilRoute />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />

                                                                                <Route
                                                                                  path="/prawo-miejscowe/*"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <LocalLawRoute />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />

                                                                                <Route
                                                                                  path="/egib/*"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <EgibRoute />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />

                                                                                <Route
                                                                                  path="/zamowienia-publiczne/*"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <ProcurementRoute />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />

                                                                                <Route
                                                                                  path="/gospodarka-odpadami/*"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <WasteMapRoute />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />

                                                                                <Route
                                                                                  path="/budget/*"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <BudgetRoute />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />

                                                                                <Route
                                                                                  path="/courtPortal/*"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <CourtPortalRoute />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />

                                                                                <Route
                                                                                  path="/helpdesk/*"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <HelpdeskRoute />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />
                                                                                <Route
                                                                                  path="/chat/*"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <ChatRoute />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />

                                                                                <Route
                                                                                  path="/contactFormVerification/*"
                                                                                  element={
                                                                                    <ContactFormVerificationRoute />
                                                                                  }
                                                                                />
                                                                                <Route
                                                                                  path="/*"
                                                                                  element={
                                                                                    <ApplicationLayout>
                                                                                      <DeskRoute />
                                                                                    </ApplicationLayout>
                                                                                  }
                                                                                />
                                                                              </Routes>
                                                                              </>
                                                                            </ProtectedRoute>
                                                                            {modals.createDossier && (
                                                                              <CreateDossierModal />
                                                                            )}
                                                                            {modals.createCase && (
                                                                              <CreateCaseModal />
                                                                            )}
                                                                            {modals.createAction && (
                                                                              <ActionModal />
                                                                            )}
                                                                            {modals.createEntity && (
                                                                              <EditClientModalNew
                                                                                label="Nowy podmiot"
                                                                                onClose={() =>
                                                                                  setModals(
                                                                                    {
                                                                                      ...modals,
                                                                                      hide: true,
                                                                                    },
                                                                                  )
                                                                                }
                                                                                // newClient={true}
                                                                              />
                                                                            )}
                                                                            {modals.createCompany && (
                                                                              <EditClientModalNew
                                                                                label="Nowy podmiot"
                                                                                onClose={() =>
                                                                                  setModals(
                                                                                    {
                                                                                      ...modals,
                                                                                      hide: true,
                                                                                    },
                                                                                  )
                                                                                }
                                                                                isCompany
                                                                                // newClient={true}
                                                                              />
                                                                            )}
                                                                            {modals.createLetter && (
                                                                              <CreateLetterModal />
                                                                            )}
                                                                            {modals.createPostage && (
                                                                              <IncomingParcelWizard />
                                                                            )}
                                                                            {modals.createPost && (
                                                                              <CreateOutgoingParcelModal />
                                                                            )}
                                                                            {modals.createTask && (
                                                                              <CreateTaskModal type="task" />
                                                                            )}
                                                                            {modals.createEvent && (
                                                                              <CreateTaskModal type="event" />
                                                                            )}
                                                                            {modals.attachCase && (
                                                                              <AttachToCaseModalNew />
                                                                            )}
                                                                            {modals.editStatus && (
                                                                              <EditStatusModal />
                                                                            )}
                                                                            {modals.editLabels && (
                                                                              <EditTagModal />
                                                                            )}
                                                                            {modals.addAddress && (
                                                                              <AddAddressModal />
                                                                            )}
                                                                            {modals.addClient && (
                                                                              <AddClientInClientsModal />
                                                                            )}
                                                                            {modals.editPDFName && (
                                                                              <EditPDFName />
                                                                            )}
                                                                            <DeepLinkHandler />
                                                                            {modals.settings && (
                                                                              <SettingsModal
                                                                                label={
                                                                                  isGuest
                                                                                    ? "E-Doręczenia"
                                                                                    : undefined
                                                                                }
                                                                              />
                                                                            )}
                                                                            {modals.initialConfig && (
                                                                              <FirstActivationWizard
                                                                                onClose={() =>
                                                                                  setModals(
                                                                                    {
                                                                                      ...modals,
                                                                                      initialConfig:
                                                                                        false,
                                                                                    },
                                                                                  )
                                                                                }
                                                                              />
                                                                            )}
                                                                            {modals.createHelpdesk && (
                                                                              <CreateHelpdeskIssueModal />
                                                                            )}
                                                                            {modals.createChat && (
                                                                              <CreateChatModal />
                                                                            )}
                                                                            {modals.createInvoice && (
                                                                              <CreateInvoiceModal />
                                                                            )}
                                                                            {modals.createBudgetExpense && (
                                                                              <CreateBudgetExpenseModal />
                                                                            )}
                                                                            {modals.createBudgetEngagement && (
                                                                              <CreateBudgetEngagementSlideModal
                                                                                initialExpenseId={(modalOptions as BudgetEngagementModalOptions)?.initialExpenseId}
                                                                              />
                                                                            )}
                                                                            {modals.createContractEntry && (
                                                                              <CreateRegistryEntryModal />
                                                                            )}
                                                                            {modals.createInvoiceRegistryEntry && (
                                                                              <CreateInvoiceRegistryModal />
                                                                            )}

                                                                            {modals.internalPass && (
                                                                              <RedirectModal />
                                                                            )}

                                                                            {modals.decree && (
                                                                              <DecreeModal />
                                                                            )}

                                                                            {modals.generateLetters && (
                                                                              <GenerateLettersModal />
                                                                            )}

                                                                            {modals.dispatch && (
                                                                              <DispatchModal />
                                                                            )}

                                                                            {modals.confirmPickup && (
                                                                              <ConfirmPickupModal />
                                                                            )}

                                                                            {modals.registerReception && (
                                                                              <RegisterReceptionModal />
                                                                            )}

                                                                            {modals.recoveryCode && (
                                                                              <RecoveryCodeModal />
                                                                            )}

                                                                            {modals.contactFormSettings && (
                                                                              <ContactFormWizard
                                                                                open={
                                                                                  modals.contactFormSettings
                                                                                }
                                                                                onClose={() => {
                                                                                  setModals(
                                                                                    {
                                                                                      ...modals,
                                                                                      contactFormSettings:
                                                                                        false,
                                                                                    },
                                                                                  );
                                                                                }}
                                                                              />
                                                                            )}

                                                                            {/* <ReactQueryDevtools /> */}
                                                                            {loading && (
                                                                              <div
                                                                                id="shado222"
                                                                                style={{
                                                                                  width:
                                                                                    "100vw",
                                                                                  height:
                                                                                    "100vh",
                                                                                  position:
                                                                                    "absolute",
                                                                                  zIndex: 1002,
                                                                                  left: 0,
                                                                                  top: 0,
                                                                                  background:
                                                                                    "rgba(0, 0, 0, 0.1)",
                                                                                }}
                                                                              >
                                                                                <Loading />
                                                                              </div>
                                                                            )}
                                                                          </SettingsContext.Provider>
                                                                        </PDFContext.Provider>
                                                                      </DecryptedMsgs.Provider>
                                                                    </DossierFilesContext.Provider>
                                                                  </CaseChatContext.Provider>
                                                                </ConverstationContext.Provider>
                                                              </CasesContext.Provider>
                                                            </LettersContext.Provider>
                                                          </ClientsContext.Provider>
                                                        </PackageThreadContext.Provider>
                                                      </PackagesContext.Provider>
                                                    </ChatMessagesContext.Provider>
                                                  </ChatsContext.Provider>
                                                </IssuesContext.Provider>
                                                </AddressBookVerificationsContext.Provider>
                                            </NotificationContext.Provider>
                                          </ActionsContext.Provider>
                                        </KeysContext.Provider>
                                      </CertContext.Provider>
                                    </MenuContext.Provider>
                                  </WorkspaceContext.Provider>
                                  </CompanyAbilityProvider>
                                </CompanyProvider>
                              </AppContext.Provider>
                            </CPluginContext.Provider>
                          </SignalsContext.Provider>
                        </ModalsManagerContext.Provider>
                      </QueryClientProvider>
                      <NotificationContainer />
                    </BrowserRouter>
                  </SignalRContext.Provider>
                </DndProvider>
              </LocalizationProvider>
            </LoadingContext.Provider>
          </ErrorBoundary>
        </EventContext.Provider>
      </AbilityContext.Provider>
    );
  }

  return (
    <>
      {auth.isLoading && <Spinner></Spinner>}
      {/* tymczasowe - jak b�dzie landing page, to nie b�dzie potrzebne */}
      {!auth.isLoading && <Unauthorized />}
    </>
  );
}
